/**
 * The standing-policy sheet as data (screens §2.6): a desk's decisions grouped under their product, each row's value chip and status words, and the
 * product header's context strip. Header facts are the Bank tab's own columns over the round-start `ProductLine`s, so a number here is the number there.
 */
import type { ContentCatalog, DecisionCatalogEntry } from "@bsim/contracts";
import { type DecisionFamily, type Decisions, type ProductLine } from "@bsim/domain";
import { BOOK_TABS, BOOK_TAB_IDS, booksOf, columnFact, tabLines, type BookTab } from "../financials/books";
import { BOOK_LABELS } from "../financials/statements";
import { swapsHeldChip, swapsHeldNoTarget, type Swap } from "../financials/swaps";
import { tradesSwaps } from "./because";
import { changeTag, oneRoundSame, optionLabel, readDecision, shortLabel } from "./decisionValues";

/** A row of the sheet: an open-able decision, or (guided rounds) one the catalog lists only as held at policy. */
/** A row of the sheet: a catalog decision, or (entry null) one a guided round holds at policy, which still names its product. */
export interface SheetRow { readonly id: string; readonly label: string; readonly entry: DecisionCatalogEntry | null; readonly productId: string | null }
export interface SheetGroup {
  /** The product's key (a decision's `productId`); null for the decisions that belong to no product. */
  readonly productId: string | null;
  readonly title: string | null;
  readonly entries: readonly DecisionCatalogEntry[];
  readonly rows: readonly SheetRow[];
}

/** The words every label starts with ("Retail money market rate", "Retail money market marketing" → "Retail money market"); a lone label reads whole. */
function commonLead(labels: readonly string[]): string {
  const words = labels.map((l) => shortLabel(l).split(" "));
  const first = words[0] ?? [];
  const lead: string[] = [];
  for (const [i, w] of first.entries()) {
    if (!words.every((ws) => ws[i] === w)) break;
    lead.push(w);
  }
  return (lead.length === 0 ? first : lead).join(" ");
}

/** A product's name: its book's name where the catalog scopes it to a book, else the words its decisions share. */
function groupTitle(entries: readonly DecisionCatalogEntry[]): string {
  const book = entries.flatMap((e) => e.scope?.books ?? [])[0];
  return (book === undefined ? undefined : BOOK_LABELS[book]) ?? commonLead(entries.map((e) => e.label));
}

/** The desk's decisions, then the ones a guided round only names as held at policy, grouped by product in order of first appearance. */
export function deskGroups(catalog: Pick<ContentCatalog, "decisions" | "policyDecisions">, family: DecisionFamily): readonly SheetGroup[] {
  const entries = catalog.decisions.filter((d) => d.family === family);
  const rows: SheetRow[] = [
    ...entries.map((entry) => ({ id: entry.decisionId, label: entry.label, entry, productId: entry.productId ?? null })),
    ...catalog.policyDecisions.filter((p) => p.family === family && !entries.some((e) => e.decisionId === p.decisionId)).map((p) => ({ id: p.decisionId, label: p.label, entry: null, productId: p.productId ?? null })),
  ];
  const keys = [...new Set(rows.map((r) => r.productId))];
  return keys.map((productId) => {
    const mine = rows.filter((r) => r.productId === productId);
    const own = mine.flatMap((r) => (r.entry === null ? [] : [r.entry]));
    return { productId, title: productId === null ? null : own.length === 0 ? commonLead(mine.map((r) => r.label)) : groupTitle(own), entries: own, rows: mine };
  });
}

/** What a product's decisions are scoped to, as the Bank's lines: those named by the scope's products or books, or by the product itself. */
export const groupLines = (entries: readonly DecisionCatalogEntry[], products: readonly ProductLine[]): readonly ProductLine[] =>
  products.filter((p) => entries.some((e) => e.scope?.products?.includes(p.productId) === true || e.scope?.books?.includes(p.bookId) === true || e.productId === p.productId));

/** The Bank columns a header repeats, per book tab, by their column names. */
const STRIP: Readonly<Record<BookTab, readonly string[]>> = {
  loans: ["Balance", "Yield", "New lending", "Net charge-offs"],
  securities: ["Balance", "Yield", "Duration", "Unrealised"],
  deposits: ["Balance", "Posted rate", "Net inflow", "Realised beta"],
  funding: ["Balance", "Rate"],
};

/** Every column of the Bank tab for these lines (`all`: every product, for the shares), with the figure the Bank tab prints: the book table behind "Book details". A figure the lines do not carry is left out, never zero. */
export function bookDetails(lines: readonly ProductLine[], all: readonly ProductLine[] = lines): ProductStrip | null {
  const first = lines[0];
  const tab = first === undefined ? undefined : BOOK_TAB_IDS.find((t) => booksOf(t).has(first.bookId));
  if (tab === undefined) return null;
  // "Share of loans" is a share of every loan on the tab, not of the lines shown: the tab's total comes from all of its lines.
  const total = tabLines(tab, all).reduce((t, l) => t + l.balanceUsd, 0);
  const facts = BOOK_TABS[tab].columns.flatMap((c) => { const fact = columnFact(c, lines, total); return fact === null ? [] : [fact]; });
  return { tab, facts };
}

export interface ProductStrip { readonly tab: BookTab; readonly facts: readonly { readonly label: string; readonly value: string }[] }

/** The context strip of a product header: the same cells the Bank tab shows for these lines; a fact the lines do not carry is left out, never zero. */
export function productStrip(lines: readonly ProductLine[]): ProductStrip | null {
  const first = lines[0];
  const tab = first === undefined ? undefined : BOOK_TAB_IDS.find((t) => booksOf(t).has(first.bookId));
  if (tab === undefined) return null;
  const facts = BOOK_TABS[tab].columns.flatMap((c) => columnFact(c, lines, 0) ?? []).filter((f) => STRIP[tab].includes(f.label));
  return { tab, facts };
}

/** The value chip of a row: the picked options, one per sub-field; a change reads "was → now". */
export function valueChip(entry: DecisionCatalogEntry, decisions: Decisions, seed: Partial<Decisions>): { readonly text: string; readonly changed: boolean } {
  const tag = changeTag(entry, decisions, seed);
  if (tag !== null) return { text: tag, changed: true };
  // A swap-trading row with no target puts on no swap, so its tenor says nothing: it is left off.
  const noTarget = tradesSwaps(entry) && readDecision(decisions, entry.decisionId, "target") === "natural";
  const picked = Object.entries(entry.options).filter(([sub]) => !(noTarget && sub === "tenorYears")).map(([sub, options]) => { const v = readDecision(decisions, entry.decisionId, sub); return v === undefined ? "—" : optionLabel(options, v); });
  return { text: picked.join(" · "), changed: false };
}

/** The standing policy's own setting of a decision, one option per sub-field: what "Standing policy: Hire with trend" says. */
export function policyText(entry: DecisionCatalogEntry, seed: Partial<Decisions>): string {
  return Object.entries(entry.options).map(([sub, options]) => { const v = readDecision(seed as Decisions, entry.decisionId, sub); return v === undefined ? "\u2014" : optionLabel(options, v); }).join(" \u00b7 ");
}

/** The chip a row prints: the value chip, with the swaps the bank holds said beside a swap-trading row (so a held swap never sits under "No hedge"). Sheet and walk print the same one. */
export function rowChip(entry: DecisionCatalogEntry, decisions: Decisions, seed: Partial<Decisions>, heldSwaps: readonly Swap[] | undefined): { readonly text: string; readonly changed: boolean } {
  const base = valueChip(entry, decisions, seed);
  if (!tradesSwaps(entry)) return base;
  const noTarget = swapsHeldNoTarget(heldSwaps, readDecision(decisions, entry.decisionId, "target"), readDecision(seed as Decisions, entry.decisionId, "target"));
  if (noTarget !== null) return { ...base, text: noTarget };
  const held = swapsHeldChip(heldSwaps);
  return held === "" ? base : { ...base, text: `${base.text} \u00b7 ${held}` };
}

/** A decision that lasts one year and goes back to its default (the dividend step, an issuance, the expense programme): it is never "same as last year". */
export const isOneRound = (entry: DecisionCatalogEntry): boolean => entry.oneRound === true;

/** The status words of an unchanged row: what last year's value was when it differs from this year's start, else that it carries on; round 1 has no last year, so it reads "Standing policy". */
export function sameStatus(entry: DecisionCatalogEntry, decisions: Decisions, lastYear: Partial<Decisions> | null, carried: boolean): string {
  if (!carried) return "Standing policy";
  if (isOneRound(entry)) return oneRoundSame(entry, valueChip(entry, decisions, decisions).text);
  const since = lastYear === null ? null : changeTag(entry, decisions, lastYear);
  return since === null ? "Same as last year" : `Last year: ${since.split(" → ")[0]}`;
}

/** What a row's status cell says, as data: held by policy, held by a limit (with its reason), changed (the row names who moved it), or unchanged (the words). */
export type RowStatus =
  | { readonly kind: "policy" }
  | { readonly kind: "constrained"; readonly reason: string }
  | { readonly kind: "changed" }
  | { readonly kind: "same"; readonly text: string };

/** The row's status: the same decision the standing-policy sheet prints, kept in one place for every face that shows a decision's current setting. */
export function rowStatus(
  entry: DecisionCatalogEntry | null,
  access: { readonly kind: "open" | "policy" | "readonly" } | { readonly kind: "constrained"; readonly reason: string },
  decisions: Decisions, seed: Partial<Decisions>, lastYear: Partial<Decisions> | null, carried: boolean,
): RowStatus {
  if (entry === null || access.kind === "policy") return { kind: "policy" };
  if (access.kind === "constrained") return { kind: "constrained", reason: access.reason };
  return valueChip(entry, decisions, seed).changed ? { kind: "changed" } : { kind: "same", text: sameStatus(entry, decisions, lastYear, carried) };
}
